import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getPersonLane,
  listInitiatives,
  listPieces,
  getPiece,
  approvePiece,
  getAttributionSummary,
  sendEmail,
  getDependencies,
  dismissTask,
  handleTaskByAi,
  getActiveSession,
  connectTasksWebSocket,
  resolveQuestDecision,
  listCampaigns,
  getInboxSummary,
  listInboxMessages,
  PersonLane as PersonLaneData,
  LaneStatus,
  LaneTask,
  InitiativeSummary,
  PieceSummary,
  Piece,
  AttributionSummary,
  Dependency,
} from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useChat } from '../context/ChatContext';
import { useIdentity, IDENTITY_PEOPLE, isRep, personLabel } from '../context/IdentityContext';
import { useTaskModal } from '../context/TaskModalContext';
import { onTaskModalChanged } from '../components/TaskModal';
import InfoModal from '../components/InfoModal';
import { listConversations, ConversationSummary } from '../services/chat.service';
import { fetchMe } from '../services/auth';
import { DependencyRow } from './Dependencies';

// The command center defaults to the GLOBAL identity's lane. An optional "view another
// lane" affordance lets you peek at someone else's command center.
const PEOPLE = IDENTITY_PEOPLE;
type PersonId = (typeof IDENTITY_PEOPLE)[number];

// ---- Quest web app deep-linking -------------------------------------------
// Base URL of the Quest web app, env-configurable. Used to build real external
// links into Quest (opened in a new tab from the cockpit).
const QUEST_WEB = (process.env.REACT_APP_QUEST_WEB_URL || 'https://quest.spiritualdata.org').replace(/\/+$/, '');

// Build a URL into Quest. With a questId, link to that quest's detail page
// (/quest/:questId per the Quest app's linking config); otherwise link to the
// main Quest goals/dashboard home (/quest).
const questUrl = (questId?: string | null): string =>
  questId ? `${QUEST_WEB}/quest/${encodeURIComponent(questId)}` : `${QUEST_WEB}/quest`;

// A subtle "Open in Quest" external link. Opens in a new tab.
const OpenInQuestLink: React.FC<{
  questId?: string | null;
  label?: string;
  color: string;
  fontSize?: string;
  fontWeight?: number;
}> = ({ questId, label = 'Open in Quest', color, fontSize = '13px', fontWeight = 600 }) => (
  <a
    href={questUrl(questId)}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    style={{ color, fontSize, fontWeight, textDecoration: 'none', whiteSpace: 'nowrap' }}
    title="Open in the Quest web app"
  >
    {label} ↗
  </a>
);

// ---- Joshua's "My Goals (Quest)" card --------------------------------------
// The cockpit tasks panel shows tasks assigned to Joshua — NOT his personal
// Quest goals. This card makes that explicit: his real goals live in Quest, and
// links there. If Quest isn't connected (the lane reports no live Quest data),
// it points him to Dependencies to connect it. We never fabricate goal data.
const MyGoalsQuestCard: React.FC<{
  lane: PersonLaneData | null;
  cardStyle: React.CSSProperties;
}> = ({ lane, cardStyle }) => {
  const { theme } = useTheme();
  // Quest is "not connected" when the lane couldn't reach live Quest data.
  const questConnected = !(lane?.decisions_note || lane?.decisions_error);
  return (
    <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.primary}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <h2 style={{ color: theme.text, margin: 0, fontSize: '18px' }}>My Goals (Quest)</h2>
        <OpenInQuestLink label="Open Quest" color={theme.primary} fontSize="14px" />
      </div>
      <p style={{ color: theme.text, margin: '8px 0 0', fontSize: '14px' }}>
        Your personal goals live in Quest — the “Tasks assigned to you” panel below is your cockpit work,
        not your goals.
      </p>
      {!questConnected && (
        <p style={{ color: theme.textSecondary, margin: '8px 0 0', fontSize: '13px' }}>
          Quest isn’t connected yet, so your goals can’t be surfaced here.{' '}
          <Link to="/dependencies" style={{ color: theme.primary, fontWeight: 600 }}>
            Connect Quest in Dependencies
          </Link>{' '}
          to surface them here.
        </p>
      )}
    </div>
  );
};

// ---- Role config: hardcoded per-person mandate map -------------------------
interface RoleConfig {
  title: string; // job title
  mandate: string; // one-line mandate
  owns: string[]; // what they own / are accountable for
  tasksLabel: string; // panel label for their task lane
  reviews: string; // what kind of asks they handle ("My Teams")
}

const ROLE_CONFIG: Record<PersonId, RoleConfig> = {
  joshua: {
    title: 'Founder',
    mandate: 'Holds the mission, the science, and the teaching.',
    owns: [
      'Mission & scientific direction',
      'Teaching: Quest course + webinar',
      'Final say on impact measures & big bets',
    ],
    tasksLabel: 'Tasks assigned to you',
    reviews: 'Decisions directed at you (the ones only the Founder can make).',
  },
  subham: {
    title: 'Director of Product',
    mandate: 'Owns product revenue and leads the Quest team.',
    owns: [
      'Quest (the product)',
      'The Concept AI / research interface',
      'The Quest course',
      'Leads the Quest team',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Product asks routed to you and your team.',
  },
  aazmeer: {
    title: 'Director of Growth',
    mandate: 'Owns donations across every gift size and runs outreach.',
    owns: [
      'Major donors / board relationships',
      'Sponsors',
      'Smaller gifts',
      'Manages an outreach team member',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Growth & donor asks routed to you and your team.',
  },
  thadeus: {
    title: 'Director of Impact',
    mandate: 'Monitors and executes impact, course-correcting from analytics.',
    owns: [
      'Impact monitoring & execution',
      'Course-correction from analytics',
      'Ads within a budget envelope',
      'Proposes impact measures (Joshua approves)',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Impact asks routed to you and your team.',
  },
  zee: {
    title: 'EA / Operations / HR + Content',
    mandate: 'The daily-reliable human and the quality filter for everything outward.',
    owns: [
      'Real-world actions',
      'Content sign-off & routine post approval',
      'HR',
      'Frontend QA — the human quality filter',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Operations, HR, and content asks routed to you.',
  },
  payal: {
    title: 'Director of AI Platform',
    mandate: 'Owns the AI platform behind Quest and the research interface.',
    owns: [
      'AI platform / Concept AI',
      'Quest backend AI integration',
      'Research interface',
      'AI platform engineering routed to her',
    ],
    tasksLabel: 'Tasks',
    reviews: 'AI platform asks routed to you and your team.',
  },
  travis: {
    title: 'Director of People & Operations',
    mandate: 'Owns people, hiring, and operations for the team.',
    owns: [
      'People & hiring',
      'Team operations',
      'Process & coordination',
      'People/ops asks routed to him',
    ],
    tasksLabel: 'Tasks',
    reviews: 'People & operations asks routed to you and your team.',
  },
  kaja: {
    title: 'Principal DevOps Engineer',
    mandate: 'Owns infrastructure, CI/CD, deploys, and reliability across the platform.',
    owns: [
      'Infrastructure & cloud',
      'CI/CD & deploys',
      'Reliability & monitoring',
      'DevOps tasks routed to her',
    ],
    tasksLabel: 'Tasks',
    reviews: 'DevOps & infrastructure asks routed to you.',
  },
  hassan: {
    title: 'Full Stack Developer',
    mandate: 'Builds and ships full-stack and frontend features on Quest.',
    owns: [
      'Full-stack feature development',
      'Frontend product and marketing pages',
      'Authentication flows',
      'Application Engineering tasks routed to him',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Engineering tasks routed to you.',
  },
  honelign: {
    title: 'Software Engineer',
    mandate: 'Builds interactive and 3D frontend experiences on Quest.',
    owns: [
      'Frontend and 3D UI (galaxy visualization)',
      'Three.js / React Three Fiber / Framer Motion',
      'Interactive quest experiences',
      'Application Engineering tasks routed to him',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Engineering tasks routed to you.',
  },
  abrham: {
    title: 'Principal Data Engineer',
    mandate: 'Builds and operates the research data platform and AI pipelines.',
    owns: [
      'Data ingestion and EL pipelines',
      'Vector store design and operations',
      'GCP data infrastructure',
      'AI Platform data tasks routed to him',
    ],
    tasksLabel: 'Tasks',
    reviews: 'AI platform data tasks routed to you.',
  },
  adeola: {
    title: 'Content Creator',
    mandate: 'Scripts and produces brand-aligned social and campaign content.',
    owns: [
      'UGC scripts and campaign posts',
      'Social content calendar',
      'Multi-platform content (Instagram, TikTok, LinkedIn, Facebook, X)',
      'Impact content tasks routed to her',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Impact content tasks routed to you.',
  },
  armin: {
    title: 'Research Data & AI Evaluation',
    mandate: 'Builds the AI evaluation harness and curates research datasets.',
    owns: [
      'AI evaluation and benchmarking',
      'Science Q&A datasets',
      'Evaluation test runner and metrics',
      'Science team tasks routed to him',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Science team tasks routed to you.',
  },
  jubaer: {
    title: 'Senior Data Engineer',
    mandate: 'Builds research data pipelines and maintains vector store quality.',
    owns: [
      'Research data extraction pipelines',
      'Data deduplication and validation',
      'Vector store analysis and documentation',
      'AI Platform data tasks routed to him',
    ],
    tasksLabel: 'Tasks',
    reviews: 'AI platform data tasks routed to you.',
  },
  prachi: {
    title: 'Data Scientist',
    mandate: 'Runs analysis, modeling, and evaluation on the research data and Concept AI.',
    owns: [
      'Data analysis and modeling',
      'Model and domain output evaluation',
      'Reproducible experiments and metrics',
      'AI Platform data-science tasks routed to her',
    ],
    tasksLabel: 'Tasks',
    reviews: 'AI platform data-science tasks routed to you.',
  },
  mary: {
    title: 'Partnership & Development',
    mandate: 'Builds the funding pipeline through prospect and grant research.',
    owns: [
      'Donor and philanthropist prospect research',
      'Grant organization research',
      'Funding database enrichment',
      'Growth prospect tasks routed to her',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Growth and prospect research tasks routed to you.',
  },
  joey: {
    title: 'Quest Product & Development Operations',
    mandate: 'Ships Quest: cuts releases and keeps the app healthy in production.',
    owns: [
      'Quest releases to main and production',
      'Quest application layer in production (config, flags, reversible migrations, restarts)',
      'Quest product architecture ahead of implementation',
      'Product and app-operations tasks routed to him',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Quest product and app-operations tasks routed to you.',
  },
  ojassvi: {
    title: 'Product Manager',
    mandate: 'Researches and specs product features for Quest.',
    owns: [
      'Product PRDs and specs',
      'Community and social feature design',
      'Product task breakdown and testing',
      'Product management tasks routed to her',
    ],
    tasksLabel: 'Tasks',
    reviews: 'Product management tasks routed to you.',
  },
};

// Which initiative domain each role owns. Joshua (Founder) sees ALL — null
// means "no domain filter". Used to scope the "Relevant initiatives" panel.
const PERSON_DOMAIN: Record<PersonId, InitiativeSummary['domain'] | null> = {
  joshua: null,
  subham: 'product',
  aazmeer: 'growth',
  thadeus: 'impact',
  zee: 'content',
  payal: 'product',
  travis: null,
  kaja: 'product',
  hassan: 'product',
  honelign: 'product',
  abrham: 'product',
  adeola: 'impact',
  armin: 'product',
  jubaer: 'product',
  prachi: 'product',
  mary: 'growth',
  ojassvi: 'product',
  joey: 'product',
};

const STATUS_ORDER: LaneStatus[] = ['queued', 'in_progress', 'ready_for_review', 'completed', 'failed'];

const STATUS_LABEL: Record<LaneStatus, string> = {
  queued: 'Queued',
  in_progress: 'In Progress',
  ready_for_review: 'Ready for Review',
  completed: 'Completed',
  failed: 'Failed',
};

const STATUS_COLOR: Record<LaneStatus, string> = {
  queued: '#6b7280',
  in_progress: '#3b82f6',
  ready_for_review: '#f59e0b',
  completed: '#10b981',
  failed: '#ef4444',
};

// ---- Role-specific feature panels ------------------------------------------
// Each role gets a DIFFERENT panel surfacing tools relevant to their mandate.
// This is the "Role Features" section: distinct per selected person.

interface RoleFeaturesProps {
  person: PersonId;
  lane: PersonLaneData | null;
  initiatives: InitiativeSummary[] | null;
  initiativesLoading: boolean;
  cardStyle: React.CSSProperties;
}

// A small labeled section header reused across feature panels.
const FeatureHeader: React.FC<{
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  color: string;
  textSecondary: string;
}> = ({ title, subtitle, right, color, textSecondary }) => (
  <div style={{ marginBottom: '12px' }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
      }}
    >
      <h2 style={{ color, margin: 0, fontSize: '18px' }}>{title}</h2>
      {right}
    </div>
    {subtitle && (
      <p style={{ color: textSecondary, margin: '4px 0 0', fontSize: '13px' }}>{subtitle}</p>
    )}
  </div>
);

const RoleFeatures: React.FC<RoleFeaturesProps> = ({
  person,
  lane,
  initiatives,
  initiativesLoading,
  cardStyle,
}) => {
  const { theme } = useTheme();

  // ---- aazmeer: donor outreach compose form --------------------------------
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<{ kind: 'ok' | 'info' | 'error'; msg: string } | null>(null);
  // Confirm-before-act gate: sending a donor email is an OUTWARD, irreversible
  // action, so it never fires on a single click. Submitting the form stages a
  // preview (HOLD-default — nothing leaves until the human explicitly confirms);
  // only "Confirm & send" actually calls sendEmail. Mirrors the OS's HOLD-default
  // decision-request pattern, made visible in the cockpit.
  const [pendingEmail, setPendingEmail] = useState<{ to: string; subject: string; body: string } | null>(null);

  // Stage the email for review instead of sending immediately.
  const handleSendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailTo.trim() || !emailSubject.trim() || !emailBody.trim()) return;
    setEmailFeedback(null);
    setPendingEmail({ to: emailTo.trim(), subject: emailSubject.trim(), body: emailBody });
  };

  // Fire only after the human explicitly confirms the preview.
  const confirmSendEmail = async () => {
    if (!pendingEmail) return;
    setEmailSending(true);
    setEmailFeedback(null);
    try {
      const html = pendingEmail.body
        .split('\n')
        .map((line) => line || '<br/>')
        .join('<br/>');
      const result = await sendEmail(pendingEmail.to, pendingEmail.subject, html);
      if (result.configured === false) {
        setEmailFeedback({
          kind: 'info',
          msg: result.note || 'Email is not configured (no Resend key). Nothing was sent.',
        });
      } else {
        setEmailFeedback({ kind: 'ok', msg: 'Sent. Your donor outreach is on its way.' });
        setEmailTo('');
        setEmailSubject('');
        setEmailBody('');
      }
      setPendingEmail(null);
    } catch (err: any) {
      setEmailFeedback({
        kind: 'error',
        msg: err.response?.data?.detail || 'Failed to send. Please try again.',
      });
    } finally {
      setEmailSending(false);
    }
  };

  // ---- thadeus: attribution summary ----------------------------------------
  const [attribution, setAttribution] = useState<AttributionSummary | null>(null);
  const [attributionLoading, setAttributionLoading] = useState(false);
  const [attributionError, setAttributionError] = useState<string | null>(null);

  useEffect(() => {
    if (person !== 'thadeus') return;
    let cancelled = false;
    setAttributionLoading(true);
    setAttributionError(null);
    getAttributionSummary()
      .then((data) => {
        if (!cancelled) setAttribution(data);
      })
      .catch((err: any) => {
        if (!cancelled)
          setAttributionError(err.response?.data?.detail || 'Failed to load attribution.');
      })
      .finally(() => {
        if (!cancelled) setAttributionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [person]);

  // ---- zee: approvals queue (pieces with status === 'ready') ---------------
  interface ReadyPiece {
    initiativeId: string;
    initiativeName: string;
    piece: PieceSummary;
  }
  const [approvals, setApprovals] = useState<ReadyPiece[] | null>(null);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalsError, setApprovalsError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const loadApprovals = useCallback(async () => {
    setApprovalsLoading(true);
    setApprovalsError(null);
    try {
      const inits = await listInitiatives();
      const results = await Promise.all(
        inits.map(async (init) => {
          try {
            const pieces = await listPieces(init.id);
            return pieces
              .filter((p) => p.status === 'ready')
              .map((p) => ({
                initiativeId: init.id,
                initiativeName: init.name || init.id,
                piece: p,
              }));
          } catch {
            return [] as ReadyPiece[];
          }
        })
      );
      setApprovals(results.flat());
    } catch (err: any) {
      setApprovalsError(err.response?.data?.detail || 'Failed to load the approvals queue.');
      setApprovals(null);
    } finally {
      setApprovalsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (person === 'zee') loadApprovals();
  }, [person, loadApprovals]);

  const handleApprove = async (initiativeId: string, pieceId: string) => {
    setApprovingId(pieceId);
    try {
      await approvePiece(initiativeId, pieceId);
      await loadApprovals();
      // If the previewed piece was the one approved, close the preview.
      setPreview((p) => (p && p.piece.id === pieceId ? null : p));
    } catch (err: any) {
      setApprovalsError(err.response?.data?.detail || 'Failed to approve. Please try again.');
    } finally {
      setApprovingId(null);
    }
  };

  // ---- zee: preview a ready piece before approving --------------------------
  // The queue rows only carry a PieceSummary (no draft/review), so opening the
  // preview fetches the full piece (draft.md + review trail + any image).
  const [preview, setPreview] = useState<{ initiativeId: string; initiativeName: string; piece: Piece } | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handleView = async (initiativeId: string, initiativeName: string, pieceId: string) => {
    setPreviewLoadingId(pieceId);
    setPreviewError(null);
    try {
      const full = await getPiece(initiativeId, pieceId);
      setPreview({ initiativeId, initiativeName, piece: full });
    } catch (err: any) {
      setPreviewError(err.response?.data?.detail || 'Failed to load the content. Please try again.');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  // ---- shared bits ---------------------------------------------------------
  const quickLinkStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '14px',
    backgroundColor: theme.background,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    textDecoration: 'none',
    minWidth: 0,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    borderRadius: '6px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '14px',
    fontFamily: 'inherit',
  };

  const noteStyle: React.CSSProperties = {
    color: theme.textSecondary,
    fontSize: '13px',
    fontStyle: 'italic',
    marginTop: '12px',
  };

  // ===== joshua: no separate role-feature panel =============================
  // Joshua's attention items (Quest decisions, asks, waiting chats) now live in
  // the single consolidated "Needs you" section above — no duplicate panel here.
  if (person === 'joshua') {
    return null;
  }

  // ===== subham: Product ====================================================
  if (person === 'subham') {
    return (
      <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.primary}` }}>
        <FeatureHeader
          title="Product"
          subtitle="Your product surfaces: releases and the feature board."
          color={theme.text}
          textSecondary={theme.textSecondary}
        />
        <div className="role-feature-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <Link to="/releases" style={quickLinkStyle}>
            <span style={{ color: theme.text, fontSize: '14px', fontWeight: 700 }}>Product releases →</span>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>Review &amp; ship the release loop.</span>
          </Link>
          <Link to="/" style={quickLinkStyle}>
            <span style={{ color: theme.text, fontSize: '14px', fontWeight: 700 }}>Features →</span>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>The feature board for Quest.</span>
          </Link>
        </div>
      </div>
    );
  }

  // ===== aazmeer: Donor outreach ============================================
  if (person === 'aazmeer') {
    return (
      <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.primary}` }}>
        <FeatureHeader
          title="Donor outreach"
          subtitle="Compose a personal note to a donor or sponsor — you preview & confirm before anything is sent."
          color={theme.text}
          textSecondary={theme.textSecondary}
        />
        <form onSubmit={handleSendEmail} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <input
            type="email"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            placeholder="To (donor email)"
            style={inputStyle}
          />
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Subject"
            style={inputStyle}
          />
          <textarea
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            placeholder="Message"
            rows={4}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
          <div>
            <button
              type="submit"
              disabled={emailSending || !!pendingEmail || !emailTo.trim() || !emailSubject.trim() || !emailBody.trim()}
              style={{
                padding: '9px 20px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor:
                  emailSending || !!pendingEmail || !emailTo.trim() || !emailSubject.trim() || !emailBody.trim()
                    ? theme.border
                    : theme.primary,
                color: '#fff',
                cursor:
                  emailSending || !!pendingEmail || !emailTo.trim() || !emailSubject.trim() || !emailBody.trim()
                    ? 'not-allowed'
                    : 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Review & send…
            </button>
          </div>
        </form>
        {/* Confirm-before-act gate: an explicit preview of exactly what will go
            out. Nothing is sent until "Confirm & send" is clicked (HOLD-default). */}
        {pendingEmail && (
          <div
            style={{
              marginTop: '14px',
              padding: '14px',
              borderRadius: '8px',
              border: `1px solid ${theme.warning}`,
              backgroundColor: theme.warning + '12',
            }}
          >
            <div style={{ color: theme.warning, fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>
              ⚠️ Review before sending — this goes out to a real recipient.
            </div>
            <div style={{ fontSize: '13px', color: theme.text, lineHeight: 1.6 }}>
              <div>
                <span style={{ color: theme.textSecondary }}>To:</span> {pendingEmail.to}
              </div>
              <div>
                <span style={{ color: theme.textSecondary }}>Subject:</span> {pendingEmail.subject}
              </div>
            </div>
            <div
              style={{
                marginTop: '8px',
                padding: '10px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.background,
                color: theme.text,
                fontSize: '13px',
                whiteSpace: 'pre-wrap',
                maxHeight: '180px',
                overflowY: 'auto',
              }}
            >
              {pendingEmail.body}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={confirmSendEmail}
                disabled={emailSending}
                style={{
                  padding: '9px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: emailSending ? theme.border : theme.primary,
                  color: '#fff',
                  cursor: emailSending ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {emailSending ? 'Sending…' : 'Confirm & send'}
              </button>
              <button
                type="button"
                onClick={() => setPendingEmail(null)}
                disabled={emailSending}
                style={{
                  padding: '9px 18px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: 'transparent',
                  color: theme.text,
                  cursor: emailSending ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Cancel — keep editing
              </button>
            </div>
          </div>
        )}
        {emailFeedback && (
          <div
            style={{
              marginTop: '12px',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor:
                emailFeedback.kind === 'ok'
                  ? theme.success + '18'
                  : emailFeedback.kind === 'error'
                  ? '#ef444418'
                  : theme.border + '44',
              border: `1px solid ${
                emailFeedback.kind === 'ok'
                  ? theme.success
                  : emailFeedback.kind === 'error'
                  ? '#ef4444'
                  : theme.border
              }`,
              color:
                emailFeedback.kind === 'ok'
                  ? theme.success
                  : emailFeedback.kind === 'error'
                  ? '#ef4444'
                  : theme.text,
            }}
          >
            {emailFeedback.msg}
          </div>
        )}
      </div>
    );
  }

  // ===== thadeus: Impact & Ads ==============================================
  if (person === 'thadeus') {
    const configured = attribution ? attribution.configured !== false : null;
    return (
      <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.primary}` }}>
        <FeatureHeader
          title="Impact & Ads"
          subtitle="Attribution at a glance, with the full analytics view one click away."
          color={theme.text}
          textSecondary={theme.textSecondary}
          right={
            <Link to="/analytics" style={{ color: theme.primary, fontSize: '14px', fontWeight: 600 }}>
              Open Analytics →
            </Link>
          }
        />
        {attributionLoading ? (
          <div style={{ color: theme.textSecondary, fontSize: '14px' }}>Loading attribution…</div>
        ) : attributionError ? (
          <div style={{ padding: '12px', backgroundColor: '#ef444422', border: '1px solid #ef4444', borderRadius: '6px', color: '#ef4444', fontSize: '13px' }}>
            {attributionError}
          </div>
        ) : configured === false ? (
          <div
            style={{
              padding: '16px',
              backgroundColor: theme.background,
              border: `1px dashed ${theme.border}`,
              borderRadius: '8px',
              color: theme.textSecondary,
              fontSize: '14px',
            }}
          >
            {attribution?.note || 'Attribution is not configured yet.'}
          </div>
        ) : attribution ? (
          (() => {
            // Surface scalar fields (numbers/strings) as readable stat tiles
            // instead of dumping raw JSON at a non-technical user. The full
            // payload stays available behind a collapsible "raw" view.
            const stats = Object.entries(attribution).filter(
              ([k, v]) => k !== 'configured' && k !== 'note' && (typeof v === 'number' || typeof v === 'string')
            );
            const prettyKey = (k: string) =>
              k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            return (
              <>
                {stats.length > 0 ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '10px',
                    }}
                  >
                    {stats.map(([k, v]) => (
                      <div
                        key={k}
                        style={{
                          padding: '12px 14px',
                          backgroundColor: theme.background,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '8px',
                        }}
                      >
                        <div style={{ color: theme.textSecondary, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {prettyKey(k)}
                        </div>
                        <div style={{ color: theme.text, fontSize: '18px', fontWeight: 700, marginTop: '4px', wordBreak: 'break-word' }}>
                          {String(v)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
                    Attribution is connected but has no metrics to show yet.
                  </div>
                )}
                <details style={{ marginTop: '12px' }}>
                  <summary style={{ color: theme.textSecondary, fontSize: '12px', cursor: 'pointer' }}>
                    Raw attribution data
                  </summary>
                  <pre
                    style={{
                      margin: '8px 0 0',
                      padding: '14px',
                      backgroundColor: theme.background,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      color: theme.text,
                      fontSize: '12px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowX: 'auto',
                    }}
                  >
                    {JSON.stringify(attribution, null, 2)}
                  </pre>
                </details>
              </>
            );
          })()
        ) : null}
        <div style={noteStyle}>Operate within the ad budget envelope — spend stays inside the agreed ceiling.</div>
      </div>
    );
  }

  // ===== zee: Approvals queue ===============================================
  if (person === 'zee') {
    return (
      <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.primary}` }}>
        <FeatureHeader
          title="Approvals queue"
          subtitle="Content pieces that are ready for your sign-off."
          color={theme.text}
          textSecondary={theme.textSecondary}
          right={
            <Link to="/initiatives" style={{ color: theme.primary, fontSize: '14px', fontWeight: 600 }}>
              Open initiatives →
            </Link>
          }
        />
        {approvalsLoading ? (
          <div style={{ color: theme.textSecondary, fontSize: '14px' }}>Loading approvals…</div>
        ) : approvalsError ? (
          <div style={{ padding: '12px', backgroundColor: '#ef444422', border: '1px solid #ef4444', borderRadius: '6px', color: '#ef4444', fontSize: '13px' }}>
            {approvalsError}
          </div>
        ) : approvals && approvals.length === 0 ? (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              color: theme.textSecondary,
              backgroundColor: theme.background,
              borderRadius: '8px',
              border: `1px dashed ${theme.border}`,
              fontSize: '14px',
            }}
          >
            Nothing waiting for approval. The queue is clear.
          </div>
        ) : approvals ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {approvals.map(({ initiativeId, initiativeName, piece }) => (
              <div
                key={`${initiativeId}/${piece.id}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: theme.text, fontSize: '14px', fontWeight: 600 }}>
                    {piece.brief || piece.type || piece.id}
                  </div>
                  <div style={{ color: theme.textSecondary, fontSize: '12px' }}>
                    {initiativeName}
                    {piece.platform ? ` · ${piece.platform}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={() => handleView(initiativeId, initiativeName, piece.id)}
                    disabled={previewLoadingId === piece.id}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: `1px solid ${theme.primary}`,
                      backgroundColor: 'transparent',
                      color: theme.primary,
                      cursor: previewLoadingId === piece.id ? 'wait' : 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {previewLoadingId === piece.id ? 'Loading…' : 'View'}
                  </button>
                  <button
                    onClick={() => handleApprove(initiativeId, piece.id)}
                    disabled={approvingId === piece.id}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: approvingId === piece.id ? theme.border : theme.success,
                      color: '#fff',
                      cursor: approvingId === piece.id ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {approvingId === piece.id ? 'Approving…' : 'Approve'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {previewError ? (
          <div style={{ marginTop: '12px', padding: '10px 12px', backgroundColor: '#ef444422', border: '1px solid #ef4444', borderRadius: '6px', color: '#ef4444', fontSize: '13px' }}>
            {previewError}
          </div>
        ) : null}

        {preview ? (
          <PiecePreviewModal
            initiativeName={preview.initiativeName}
            piece={preview.piece}
            initiativeId={preview.initiativeId}
            approving={approvingId === preview.piece.id}
            onApprove={() => handleApprove(preview.initiativeId, preview.piece.id)}
            onClose={() => setPreview(null)}
          />
        ) : null}
      </div>
    );
  }

  return null;
};

// ---- Preview modal: shows a ready piece's content before sign-off ----------
// Reuses the same draft/iteration-trail/image rendering the Initiatives page
// uses, so the approver sees exactly what they're approving without leaving
// their lane. The Approve action is mirrored here for convenience.
const PiecePreviewModal: React.FC<{
  initiativeId: string;
  initiativeName: string;
  piece: Piece;
  approving: boolean;
  onApprove: () => void;
  onClose: () => void;
}> = ({ initiativeId, initiativeName, piece, approving, onApprove, onClose }) => {
  const { theme } = useTheme();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hasDraft = !!(piece.draft && piece.draft.trim());
  const hasImage = !!(piece.image && (piece.image.url || piece.image.b64_file));
  const title = piece.brief || piece.type || piece.id;

  const preStyle: React.CSSProperties = {
    backgroundColor: theme.background,
    border: `1px solid ${theme.border}`,
    borderRadius: '6px',
    padding: '12px',
    color: theme.text,
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    maxHeight: '320px',
    overflowY: 'auto',
    margin: 0,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        zIndex: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Preview: ${title}`}
        style={{
          width: 'min(720px, 100%)',
          maxHeight: '85vh',
          overflowY: 'auto',
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          padding: 22,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, color: theme.text, fontSize: 18 }}>{title}</h3>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
              {initiativeName}
              {piece.platform ? ` · ${piece.platform}` : ''}
              {piece.type ? ` · ${piece.type}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* Image pieces show the generated image; text pieces show the draft. */}
        {hasImage ? (
          <div style={{ marginTop: 12 }}>
            {piece.image_prompt && piece.image_prompt.trim() ? (
              <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 6, whiteSpace: 'pre-wrap' }}>{piece.image_prompt}</div>
            ) : null}
            <img
              src={piece.image?.url
                ? piece.image.url
                : `${process.env.REACT_APP_API_URL || '/api'}/content/initiatives/${initiativeId}/pieces/${piece.id}/image`}
              alt={piece.image_prompt || 'Generated image'}
              style={{ maxWidth: '100%', borderRadius: 6, border: `1px solid ${theme.border}` }}
            />
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <div style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Content
            </div>
            {hasDraft ? (
              <pre style={preStyle}>{piece.draft}</pre>
            ) : (
              <div style={{ ...preStyle, color: theme.textSecondary, textAlign: 'center' }}>
                No content was generated for this piece.
              </div>
            )}
          </div>
        )}

        {piece.review && piece.review.trim() ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {`Iteration trail${typeof piece.iterations === 'number' ? ` (${piece.iterations})` : ''}`}
            </div>
            <pre style={{ ...preStyle, maxHeight: '240px' }}>{piece.review}</pre>
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${theme.border}`,
              backgroundColor: 'transparent',
              color: theme.text,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={onApprove}
            disabled={approving}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: approving ? theme.border : theme.success,
              color: '#fff',
              cursor: approving ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {approving ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Frontend QA note (distinct from content approvals) --------------------
// Frontend QA is a SEPARATE responsibility from content sign-off: it runs through
// QuickTasks, not the approvals queue. Surfaced as its own note so the two never mix.
const FrontendQANote: React.FC<{ cardStyle: React.CSSProperties }> = ({ cardStyle }) => {
  const { theme } = useTheme();
  return (
    <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.info}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <h2 style={{ color: theme.text, margin: 0, fontSize: '18px' }}>Frontend QA</h2>
        <Link to="/quicktasks" style={{ color: theme.primary, fontSize: '14px', fontWeight: 600 }}>
          Open Quick Tasks →
        </Link>
      </div>
      <p style={{ color: theme.text, margin: '8px 0 0', fontSize: '14px' }}>
        Frontend QA is separate — open the running app and file a QuickTask for any defect
        (the AI fixes it; you re-check). This is distinct from content sign-off above.
      </p>
    </div>
  );
};

// ---- "What's my AI doing right now": a subtle active/heartbeat indicator -----
// Status counts answer "what's queued / done"; they do NOT answer "is the AI
// running THIS SECOND". This badge distinguishes a task actively running/streaming
// (live) from one merely sitting in `in_progress`.
//
// Signal available without new backend: the cockpit's live Claude Code session
// (`GET /sessions/active`) reports `last_modified` (epoch seconds) of the session
// file — it advances while the AI streams output. So we treat the lane as "active
// now" when it HAS in-progress work AND the session file changed within a short
// freshness window. Otherwise we show "idle" (in_progress, not currently streaming).
// Polls lightly; degrades silently to nothing if the endpoint is unavailable.
const ACTIVE_FRESHNESS_SECONDS = 90;

const ActiveNowBadge: React.FC<{ inProgressCount: number }> = ({ inProgressCount }) => {
  const { theme } = useTheme();
  const [lastModified, setLastModified] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      getActiveSession()
        .then((s) => {
          if (cancelled) return;
          setLastModified(s.active && typeof s.last_modified === 'number' ? s.last_modified : null);
          setNow(Date.now());
        })
        .catch(() => {
          if (!cancelled) setLastModified(null);
        });
    };
    poll();
    const id = window.setInterval(poll, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // No in-progress work → nothing to indicate.
  if (inProgressCount <= 0) return null;

  const ageSeconds = lastModified != null ? now / 1000 - lastModified : Infinity;
  const live = ageSeconds <= ACTIVE_FRESHNESS_SECONDS;

  return (
    <span
      title={
        live
          ? 'Your AI is actively working right now (live session output is streaming).'
          : 'Work is in progress but the AI is not streaming output this moment.'
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '3px 10px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 700,
        backgroundColor: live ? STATUS_COLOR.in_progress + '22' : theme.border + '44',
        color: live ? STATUS_COLOR.in_progress : theme.textSecondary,
        border: `1px solid ${live ? STATUS_COLOR.in_progress : theme.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        className={live ? 'active-now-pulse' : undefined}
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: live ? STATUS_COLOR.in_progress : theme.textSecondary,
          display: 'inline-block',
        }}
      />
      {live ? 'AI working now' : 'In progress · idle'}
      <style>{`
        @keyframes active-now-pulse-kf {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(0.7); }
          100% { opacity: 1; transform: scale(1); }
        }
        .active-now-pulse { animation: active-now-pulse-kf 1.2s ease-in-out infinite; }
      `}</style>
    </span>
  );
};


// ---- Chat with this person's AI (compact launcher) --------------------------
// The heavy inline chat panel was extracted into the shared ChatDock (pop-out drawer +
// /chat full page) to declutter the command center. This compact card just opens a DIRECT
// conversation with the selected person's AI rep in the drawer (create-or-open).
const ChatLauncher: React.FC<{
  person: string;
  cardStyle: React.CSSProperties;
}> = ({ person, cardStyle }) => {
  const { theme } = useTheme();
  const { requestDirectChat } = useChat();
  return (
    <div style={{ ...cardStyle, marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ color: theme.text, margin: '0 0 4px', fontSize: '18px' }}>
            Chat with {person}'s AI
          </h2>
          <p style={{ color: theme.textSecondary, margin: 0, fontSize: '13px' }}>
            A real back-and-forth conversation in the chat panel. Ask it to actually do
            something and it spins that up as a task and links you to it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => requestDirectChat(person)}
          style={{
            padding: '10px 18px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: theme.primary,
            color: '#fff',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          💬 Chat with {person}'s AI
        </button>
      </div>
      <p style={{ color: theme.textSecondary, margin: '10px 0 0', fontSize: '12px' }}>
        Want a group room with several reps and people? Open the{' '}
        <Link to="/chat" style={{ color: theme.primary, fontWeight: 600 }}>full chat page →</Link>
      </p>
    </div>
  );
};

// ---- DecisionRow: Interactive Quest decision response ----------------------
// Displays a Quest decision with approve/decline buttons AND a text input field
// for providing clarifications or additional context when responding.
const DecisionRow: React.FC<{
  decision: any;
  onResolved: () => void;
  theme: any;
}> = ({ decision, onResolved, theme }) => {
  const [textInput, setTextInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doResolve = async (resolution: string) => {
    setSaving(true);
    setError(null);
    try {
      const decisionId = decision?.id || decision?.decision_id;
      if (!decisionId) throw new Error('Decision ID not found');
      await resolveQuestDecision(decisionId, resolution);
      onResolved();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to resolve. Please try again.');
      setSaving(false);
    }
  };

  const btn = (bg: string): React.CSSProperties => ({
    padding: '8px 14px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: bg,
    color: '#fff',
    cursor: saving ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  });

  return (
    <div
      style={{
        padding: '12px',
        backgroundColor: theme.background,
        border: `1px solid ${theme.border}`,
        borderRadius: '8px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '8px',
          marginBottom: '8px',
        }}
      >
        <span style={{ color: theme.text, fontSize: '14px', minWidth: 0 }}>
          {decision?.summary || decision?.title || decision?.text || decision?.question || decision?.prompt || 'Decision waiting on you.'}
        </span>
        {(decision?.quest_id || decision?.questId) && (
          <OpenInQuestLink questId={decision.quest_id || decision.questId} color={theme.primary} fontSize="12px" />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <textarea
          value={textInput}
          disabled={saving}
          rows={2}
          placeholder="Optional: add context or clarification with your response..."
          onChange={(e) => {
            setTextInput(e.target.value);
            setError(null);
          }}
          style={{
            flex: 'none',
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 10px',
            borderRadius: '6px',
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.background,
            color: theme.text,
            fontSize: '13px',
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
          aria-label="Add context with your decision"
        />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => doResolve(textInput.trim() || 'Approved.')}
            disabled={saving}
            style={btn(saving ? theme.border : theme.success)}
          >
            {saving ? 'Saving...' : 'Approve'}
          </button>
          <button
            type="button"
            onClick={() => doResolve(textInput.trim() || 'Declined.')}
            disabled={saving}
            style={btn(saving ? theme.border : '#ef4444')}
          >
            Decline
          </button>
        </div>
        {error && (
          <div style={{ color: '#ef4444', fontSize: '12px' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

// ---- "Needs you": the SINGLE attention aggregator --------------------------
// One place that answers "when do I step in?" for the current identity. It folds
// together every source that requires this person's attention:
//   (a) Quest decisions directed at them (when Quest is connected),
//   (b) cockpit dependencies/asks directed at them (questions, approvals, keys,
//       …) — resolved IN-PLACE via the shared DependencyRow,
//   (c) chats where awaiting_human == them (each opens in the chat drawer).
// This replaces the former separate "Decisions for you" and "My Teams (asks)"
// sections. Quest-not-connected degrades to a SMALL muted footnote, never a
// scary standalone panel: the section still works from cockpit asks + chats.
const NeedsYou: React.FC<{
  person: string;
  lane: PersonLaneData | null;
  cardStyle: React.CSSProperties;
}> = ({ person, lane, cardStyle }) => {
  const { theme } = useTheme();
  const { requestOpenConversation } = useChat();
  const [waitingChats, setWaitingChats] = useState<ConversationSummary[]>([]);
  const [deps, setDeps] = useState<Dependency[]>([]);

  useEffect(() => {
    let cancelled = false;
    listConversations()
      .then((convs) => {
        if (cancelled) return;
        setWaitingChats(
          convs.filter((c) => (c.awaiting_human || '').toLowerCase() === person.toLowerCase())
        );
      })
      .catch(() => {
        if (!cancelled) setWaitingChats([]);
      });
    return () => {
      cancelled = true;
    };
  }, [person]);

  // Cockpit dependencies/asks directed at this person — resolved inline below.
  const reloadDeps = useCallback(() => {
    let cancelled = false;
    getDependencies(person, 'open')
      .then((items) => {
        if (!cancelled) setDeps(items);
      })
      .catch(() => {
        if (!cancelled) setDeps([]);
      });
    return () => {
      cancelled = true;
    };
  }, [person]);

  useEffect(() => reloadDeps(), [reloadDeps]);

  const decisions = lane?.decisions ?? [];
  // Quest is "not connected" when the lane couldn't reach live Quest data. We
  // degrade to a small footnote rather than a duplicate empty section.
  const questNote = lane?.decisions_note || lane?.decisions_error || null;
  const totalNeeds = decisions.length + deps.length + waitingChats.length;

  return (
    <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.warning}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <h2 style={{ color: theme.text, margin: '0 0 4px', fontSize: '18px' }}>Needs you</h2>
        <OpenInQuestLink color={theme.primary} fontSize="13px" />
      </div>
      <p style={{ color: theme.textSecondary, margin: '0 0 12px', fontSize: '13px' }}>
        Everything waiting on you in one place: decisions and asks directed at you, plus
        chats waiting on your reply. {totalNeeds} open.
      </p>
      {totalNeeds === 0 ? (
        <div
          style={{
            padding: '16px',
            textAlign: 'center',
            color: theme.textSecondary,
            backgroundColor: theme.background,
            borderRadius: '8px',
            border: `1px dashed ${theme.border}`,
            fontSize: '14px',
          }}
        >
          Nothing needs you right now. Enjoy the calm.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Chats waiting on you — each opens in the chat drawer. */}
          {waitingChats.map((c) => (
            <button
              key={`chat-${c.id}`}
              onClick={() => requestOpenConversation(c.id)}
              style={{
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                padding: '12px',
                backgroundColor: theme.warning + '12',
                border: `1px solid ${theme.warning}`,
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              <span style={{ color: theme.text, fontSize: '14px', minWidth: 0 }}>
                💬 {c.title}
                {c.last_message ? ` — ${c.last_message.preview}` : ''}
              </span>
              <span style={{ color: theme.warning, fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                Open chat →
              </span>
            </button>
          ))}
          {/* Cockpit dependencies/asks — resolved IN-PLACE (answer / let my AI
              handle / provide a key) via the shared DependencyRow. */}
          {deps.map((d) => (
            <DependencyRow key={`dep-${d.id}`} dep={d} onResolved={reloadDeps} compact />
          ))}
          {/* Quest decisions directed at you (only the Founder can decide). */}
          {decisions.map((d: any, idx: number) => (
            <DecisionRow key={`dec-${d?.id ?? idx}`} decision={d} onResolved={reloadDeps} theme={theme} />
          ))}
        </div>
      )}
      {/* Quest-not-connected: a SMALL muted footnote, not a standalone panel.
          The section above still works from cockpit asks + waiting chats. */}
      {questNote && (
        <p style={{ color: theme.textSecondary, margin: '10px 0 0', fontSize: '12px', fontStyle: 'italic' }}>
          (Quest decisions not connected yet —{' '}
          <Link to="/dependencies" style={{ color: theme.primary, fontWeight: 600 }}>
            connect Quest
          </Link>
          .)
        </p>
      )}
    </div>
  );
};

// ---- "Email responses": the signal that a campaign reply, a reply to one of this person's
//      sent emails, or new mail in their own personal inbox has landed. EVERY team member now has
//      their own monitored mailbox (Joshua, 2026-09-11: "I want every account to have its
//      privacy" -- each person's AI reads only their own mail). Only rendered on the viewer's OWN
//      lane (see the render call site below) since the backend scopes the data to whoever is
//      really signed in, not to whichever lane happens to be open. ----
const EmailResponsesCard: React.FC<{
  person: string;
  cardStyle: React.CSSProperties;
}> = ({ person, cardStyle }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [relevant, setRelevant] = useState(false);
  const [campaignUnseen, setCampaignUnseen] = useState<{ id: number; name: string; unseen: number }[]>([]);
  const [draftUnseen, setDraftUnseen] = useState(0);
  const [mailboxUnseen, setMailboxUnseen] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listCampaigns({ person }).catch(() => ({ count: 0, campaigns: [] })),
      getInboxSummary().catch(() => null),
      listInboxMessages({ draft_reply_only: true, limit: 200 }).catch(() => ({ count: 0, messages: [] })),
    ]).then(([campaignsRes, summary, draftsRes]) => {
      if (cancelled) return;

      const ownedCampaignIds = new Set(campaignsRes.campaigns.map((c) => c.id));
      const perCampaign: { id: number; name: string; unseen: number }[] = [];
      if (summary) {
        Object.entries(summary.by_campaign).forEach(([id, counts]) => {
          if (ownedCampaignIds.has(Number(id)) && counts.unseen > 0) {
            perCampaign.push({ id: Number(id), name: counts.campaign_name || `Campaign #${id}`, unseen: counts.unseen });
          }
        });
        perCampaign.sort((a, b) => b.unseen - a.unseen);
      }
      setCampaignUnseen(perCampaign);

      setDraftUnseen(
        draftsRes.messages.filter(
          (m) => !m.seen_at && (m.email_draft_person || '').toLowerCase() === person.toLowerCase()
        ).length
      );

      // Everyone now has their own monitored mailbox (Joshua, 2026-09-11). The backend already
      // scopes getInboxSummary() to mailboxes THIS signed-in caller may see -- their own personal
      // mailbox plus the one shared support@ inbox -- and this card only renders on your own lane
      // (see the PersonLane render call site), so anything in by_mailbox that isn't the shared
      // inbox must be this person's own personal mailbox. No frontend person->email map needed.
      const personalMailboxUnseen = summary
        ? Object.entries(summary.by_mailbox)
            .filter(([mailbox]) => mailbox !== 'support@spiritualdata.org')
            .reduce((sum, [, counts]) => sum + counts.unseen, 0)
        : 0;
      setMailboxUnseen(personalMailboxUnseen);
      setRelevant(true);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [person]);

  if (loading || !relevant) return null;

  const total = campaignUnseen.reduce((sum, c) => sum + c.unseen, 0) + draftUnseen + mailboxUnseen;

  return (
    <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.info}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <h2 style={{ color: theme.text, margin: '0 0 4px', fontSize: '18px' }}>Email responses</h2>
        <Link to="/responses" style={{ color: theme.primary, fontWeight: 600, fontSize: '13px' }}>
          Open Email Responses →
        </Link>
      </div>
      {total === 0 ? (
        <p style={{ color: theme.textSecondary, margin: '4px 0 0', fontSize: '13px' }}>
          No unseen replies right now.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
          {campaignUnseen.map((c) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: theme.text }}>
              <span>{c.name}</span>
              <span style={{ fontWeight: 700, color: theme.info }}>{c.unseen} unseen</span>
            </div>
          ))}
          {draftUnseen > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: theme.text }}>
              <span>Replies to your sent emails</span>
              <span style={{ fontWeight: 700, color: theme.info }}>{draftUnseen} unseen</span>
            </div>
          )}
          {mailboxUnseen > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: theme.text }}>
              <span>Your email inbox</span>
              <span style={{ fontWeight: 700, color: theme.info }}>{mailboxUnseen} unseen</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PersonLane: React.FC = () => {
  const { theme } = useTheme();
  const { currentPerson } = useIdentity();
  const [searchParams, setSearchParams] = useSearchParams();
  const personParam = searchParams.get('person');

  // Default to the GLOBAL identity's lane; ?person= lets you view another lane.
  // Lanes only exist for the core reps. If the global identity isn't a rep
  // (any free-form name), don't force a lane — show a gentle rep-picker instead.
  const defaultLane = (): string | null =>
    personParam || (isRep(currentPerson) ? currentPerson.trim().toLowerCase() : null);
  const [person, setPerson] = useState<string | null>(defaultLane);
  const [lane, setLane] = useState<PersonLaneData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Who is REALLY signed in (the auth session, not the freely-switchable "You are" identity
  // above) -- the backend scopes /api/inbox/* to this person's own mailbox no matter which lane
  // is being viewed, so the Email Responses card below must only render on your OWN lane; showing
  // it while browsing someone else's lane would otherwise display YOUR mail under THEIR name.
  const [myRepSlug, setMyRepSlug] = useState<string | null>(null);
  const [authIsEnabled, setAuthIsEnabled] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((res) => {
        if (cancelled) return;
        setAuthIsEnabled(res.auth_enabled);
        setMyRepSlug(res.user?.rep_slug || null);
      })
      .catch(() => {
        /* fail closed: if we can't confirm who's signed in, don't assume it's an open dev box */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Initiatives state
  const [initiatives, setInitiatives] = useState<InitiativeSummary[] | null>(null);
  const [initiativesLoading, setInitiativesLoading] = useState(false);
  const [initiativesError, setInitiativesError] = useState<string | null>(null);

  const loadLane = useCallback(async (who: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPersonLane(who);
      setLane(data);
    } catch (err: any) {
      console.error('Failed to load person lane', err);
      setError(err.response?.data?.detail || 'Failed to load lane');
      setLane(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInitiatives = useCallback(async () => {
    setInitiativesLoading(true);
    setInitiativesError(null);
    try {
      const data = await listInitiatives();
      setInitiatives(data);
    } catch (err: any) {
      console.error('Failed to load initiatives', err);
      setInitiativesError(err.response?.data?.detail || 'Failed to load initiatives');
      setInitiatives(null);
    } finally {
      setInitiativesLoading(false);
    }
  }, []);

  // Sync selection from URL on mount / param change. With no ?person= override,
  // the lane follows the GLOBAL identity (the navbar "who am I").
  useEffect(() => {
    setPerson(personParam || (isRep(currentPerson) ? currentPerson.trim().toLowerCase() : null));
  }, [personParam, currentPerson]);

  // Fetch whenever a person is selected. Clear any optimistic dismissals so a
  // freshly-loaded lane isn't hiding items from a previous person/refresh.
  useEffect(() => {
    if (person) {
      setDismissedIds(new Set());
      loadLane(person);
      loadInitiatives();
    } else {
      setLane(null);
      setInitiatives(null);
    }
  }, [person, loadLane, loadInitiatives]);

  // When the shared task modal changes/removes a task (dismiss/delete/done/
  // handled/respond), reload this lane so the change is reflected here.
  useEffect(() => {
    return onTaskModalChanged(() => {
      if (person) loadLane(person);
    });
  }, [person, loadLane]);

  // Live: when ANY task changes anywhere (including one an agent creates from chat /
  // an autonomous run via manage_task.py), refetch this lane so it shows up immediately.
  useEffect(() => {
    const sub = connectTasksWebSocket(() => {
      if (person) loadLane(person);
    });
    return () => sub.close();
  }, [person, loadLane]);

  const handleSelect = (who: string) => {
    setSearchParams({ person: who });
  };

  const role: RoleConfig | null =
    person && (PEOPLE as readonly string[]).includes(person)
      ? ROLE_CONFIG[person as PersonId]
      : null;

  const isJoshua = person === 'joshua';

  // ---- In-place task action modal + optimistic dismissal -------------------
  // Clicking a task opens its detail in the SHARED, app-root task modal (no
  // navigation) via the global TaskModal context.
  // `dismissedIds` hides a task from the lane immediately when dismissed/deleted
  // (optimistic) before the lane refetch lands.
  const { openTask } = useTaskModal();
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());
  // Tracks which task's inline "Let my AI handle" is in flight.
  const [inlineAiTaskId, setInlineAiTaskId] = useState<number | null>(null);
  // Per-task result of "Let my AI handle" so the click NEVER looks dead: on 'handled' a brief
  // success note before the row clears; on 'drafted' the rep's proposed answer shown inline.
  const [inlineAiResult, setInlineAiResult] = useState<
    Record<number, { outcome: 'handled' | 'drafted'; message: string; draft?: string | null; agent?: string }>
  >({});
  // Per-status collapse for the tasks panel so long lanes stay short. Finished
  // work (completed/failed) starts collapsed; active work starts open.
  const [collapsedStatuses, setCollapsedStatuses] = useState<Set<LaneStatus>>(
    () => new Set<LaneStatus>(['completed', 'failed'])
  );
  const toggleStatusCollapsed = useCallback((s: LaneStatus) => {
    setCollapsedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);

  const optimisticallyRemove = useCallback((id: number) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleInlineDismiss = useCallback(
    async (id: number) => {
      optimisticallyRemove(id); // remove from lane immediately
      try {
        await dismissTask(id);
      } catch {
        // On failure, refetch so the item reappears (we don't keep a stale hide).
        if (person) loadLane(person);
      }
    },
    [optimisticallyRemove, person, loadLane]
  );

  const handleInlineAi = useCallback(
    async (id: number) => {
      setInlineAiTaskId(id);
      try {
        const res = await handleTaskByAi(id);
        if (res.outcome === 'handled') {
          // Taken on by the rep: show a brief success note, THEN clear it from the lane so the
          // click clearly did something (never silently vanishes).
          setInlineAiResult((prev) => ({
            ...prev,
            [id]: {
              outcome: 'handled',
              message: res.agent
                ? `✓ ${personLabel(res.agent)}'s AI answered & cleared this`
                : res.message || '✓ Your AI rep took this on',
              agent: res.agent,
            },
          }));
          window.setTimeout(() => {
            optimisticallyRemove(id);
            setInlineAiResult((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
          }, 1600);
        } else {
          // Drafted a proposed answer: surface it inline (the item stays so it can be reviewed).
          setInlineAiResult((prev) => ({
            ...prev,
            [id]: {
              outcome: 'drafted',
              message: res.message || 'Your AI rep drafted a proposed answer for your review.',
              draft: res.draft,
              agent: res.agent,
            },
          }));
        }
      } catch (err: any) {
        setInlineAiResult((prev) => ({
          ...prev,
          [id]: {
            outcome: 'drafted',
            message: err.response?.data?.detail || 'Your AI rep could not handle this right now.',
          },
        }));
      } finally {
        setInlineAiTaskId(null);
      }
    },
    [optimisticallyRemove]
  );

  // Filter the "Relevant initiatives" panel by the selected person's domain.
  // Joshua (Founder) sees everything (domain === null). Others see only their
  // domain via the `domain` field on each initiative.
  const personDomain: InitiativeSummary['domain'] | null =
    person && (PEOPLE as readonly string[]).includes(person)
      ? PERSON_DOMAIN[person as PersonId]
      : null;
  const relevantInitiatives: InitiativeSummary[] | null = initiatives
    ? personDomain == null
      ? initiatives
      : initiatives.filter((init) => init.domain === personDomain)
    : null;

  // ---- Shared card style helper --------------------------------------------
  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    padding: '20px',
    minWidth: 0,
  };

  return (
    <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px', overflowX: 'hidden' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h1 style={{ color: theme.text, margin: 0 }}>AI Command Center</h1>
          <InfoModal
            title="AI Command Center"
            body={[
              "This is where you actually GET THINGS DONE with live dev-server context — your work, your team's asks, and a direct line to your AI rep.",
              "It defaults to YOUR lane (set who you are in the navbar). \"Needs you\" gathers everything waiting on you in one place.",
              "Quest is for goals and schedule; this is for doing the work.",
            ]}
          />
        </div>
        <p style={{ color: theme.textSecondary, margin: '4px 0 0', fontSize: '14px' }}>
          {person ? (
            <>
              Showing <strong style={{ color: theme.text, textTransform: 'capitalize' }}>{person}</strong>'s
              command center. Their mandate, what needs them, a direct line to the AI rep, the
              work, and relevant initiatives.
            </>
          ) : (
            <>
              Lanes exist for the core reps. You're acting as{' '}
              <strong style={{ color: theme.text }}>{personLabel(currentPerson)}</strong> — pick a
              rep's lane below to view their command center.
            </>
          )}
        </p>
      </div>

      {/* View another lane. Defaults to the global identity when it's a rep; for a
          non-rep identity, this becomes the rep-picker to choose a lane to view. */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center' }}>
        <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: 600 }}>
          {person ? 'View lane:' : 'Viewing lane for:'}
        </span>
        {PEOPLE.map((who) => {
          const active = person === who;
          const isMe = who === currentPerson.trim().toLowerCase();
          return (
            <button
              key={who}
              onClick={() => handleSelect(who)}
              title={isMe ? 'Your lane (global identity)' : `View ${who}'s lane`}
              style={{
                padding: '8px 16px',
                borderRadius: '999px',
                border: `1px solid ${active ? theme.primary : theme.border}`,
                backgroundColor: active ? theme.primary : theme.surface,
                color: active ? '#fff' : theme.text,
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              {who}{isMe ? ' (you)' : ''}
            </button>
          );
        })}
      </div>

      {/* ---- Role header --------------------------------------------------- */}
      {person && role && (
        <div style={{ ...cardStyle, marginBottom: '16px', borderLeft: `4px solid ${theme.primary}` }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
            <h2 style={{ color: theme.text, margin: 0, fontSize: '22px', textTransform: 'capitalize' }}>
              {person}
            </h2>
            <span
              style={{
                color: theme.primary,
                fontSize: '13px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {role.title}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Link
                to="/ai-representatives"
                style={{
                  color: theme.primary,
                  fontSize: '13px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
                title="Edit this person's AI representative behavior"
              >
                Edit AI Rep ↗
              </Link>
              <OpenInQuestLink label="Open Quest" color={theme.primary} />
            </span>
          </div>
          <p style={{ color: theme.text, margin: '8px 0 12px', fontSize: '15px' }}>{role.mandate}</p>
          <div>
            <div
              style={{
                color: theme.textSecondary,
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '6px',
              }}
            >
              Owns
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {role.owns.map((item) => (
                <span
                  key={item}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '999px',
                    backgroundColor: theme.background,
                    border: `1px solid ${theme.border}`,
                    color: theme.text,
                    fontSize: '13px',
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---- Joshua only: clarify that his real goals live in Quest, not in
              the cockpit tasks panel below. ------------------------------- */}
      {person && role && isJoshua && <MyGoalsQuestCard lane={lane} cardStyle={cardStyle} />}

      {/* ---- "Needs you": the SINGLE place answering "when do I step in?" —
              Quest decisions + cockpit dependencies/asks + waiting chats. ---- */}
      {person && role && <NeedsYou person={person} lane={lane} cardStyle={cardStyle} />}

      {/* ---- Email responses: campaign replies + replies to this person's sent emails
              (+ their own personal mailbox directly, for everyone with one). Hidden when
              nothing's relevant, AND hidden entirely on someone else's lane -- the backend
              scopes the data to whoever is REALLY signed in, so showing it on another
              person's lane would otherwise display your own mail under their name. ---- */}
      {person && (!authIsEnabled || (myRepSlug && person.toLowerCase() === myRepSlug.toLowerCase())) && (
        <EmailResponsesCard person={person} cardStyle={cardStyle} />
      )}

      {/* ---- Role-specific feature panel (DIFFERENT per role) -------------- */}
      {person && role && (
        <RoleFeatures
          person={person as PersonId}
          lane={lane}
          initiatives={initiatives}
          initiativesLoading={initiativesLoading}
          cardStyle={cardStyle}
        />
      )}

      {/* ---- Frontend QA: a SEPARATE note from content approvals (Zee) ----- */}
      {person === 'zee' && <FrontendQANote cardStyle={cardStyle} />}


      {/* ---- Chat with this person's AI (opens the shared pop-out drawer) -- */}
      {person && <ChatLauncher person={person} cardStyle={cardStyle} />}

      {person && loading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '20vh',
            color: theme.text,
          }}
        >
          Loading {person}'s lane…
        </div>
      )}

      {person && !loading && error && (
        <div
          style={{
            padding: '16px',
            backgroundColor: '#ef444422',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#ef4444',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {/* ---- Tasks panel --------------------------------------------------
              The former "My Teams (asks)" sibling panel was removed: asks/
              decisions directed at this person now live in the single "Needs
              you" section above, so they're no longer duplicated here. -------- */}
      {person && !loading && !error && lane && (
        <div style={{ marginBottom: '16px' }}>
          {/* Tasks / My Goals panel */}
          <div className="card" style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
              <h2 style={{ color: theme.text, margin: 0, fontSize: '18px' }}>
                {role ? role.tasksLabel : isJoshua ? 'My Goals' : 'Tasks'}
              </h2>
              {/* "What's my AI doing right now" — live heartbeat, only when there's
                  in-progress work. Subtle; absent when nothing is running. */}
              <ActiveNowBadge inProgressCount={lane.tasks.counts.in_progress ?? 0} />
            </div>
            <p style={{ color: theme.textSecondary, margin: '0 0 16px', fontSize: '13px' }}>
              {lane.tasks.total} total
            </p>

            {/* Status counts */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {STATUS_ORDER.map((s) => (
                <div
                  key={s}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    backgroundColor: STATUS_COLOR[s] + '22',
                    color: STATUS_COLOR[s],
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {STATUS_LABEL[s]}: {lane.tasks.counts[s] ?? 0}
                </div>
              ))}
            </div>

            {/* Lists by status. Each task is a clickable card that opens its
                detail IN-PLACE (a modal on this page) where the human reads the
                AI output and acts — no navigation away. Each row also carries
                inline "Let my AI handle" and "dismiss (✕)" controls so nothing
                is ever trapped. */}
            {lane.tasks.total - dismissedIds.size <= 0 ? (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: theme.textSecondary,
                  backgroundColor: theme.background,
                  borderRadius: '8px',
                  border: `1px dashed ${theme.border}`,
                  fontSize: '14px',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '4px', color: theme.text }}>
                  Nothing assigned to you yet.
                </div>
                <div>Use “Ask your AI / give input” above to create work.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {STATUS_ORDER.map((s) => {
                  const allItems: LaneTask[] = lane.tasks.by_status[s] || [];
                  // Hide tasks the user just dismissed/handled (optimistic).
                  const items = allItems.filter((t) => t.id == null || !dismissedIds.has(t.id));
                  if (items.length === 0) return null;
                  const collapsed = collapsedStatuses.has(s);
                  return (
                    <div key={s}>
                      <button
                        type="button"
                        onClick={() => toggleStatusCollapsed(s)}
                        aria-expanded={!collapsed}
                        title={collapsed ? 'Show these tasks' : 'Hide these tasks'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          width: '100%',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          margin: '0 0 6px',
                          cursor: 'pointer',
                          color: STATUS_COLOR[s],
                          fontSize: '12px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          textAlign: 'left',
                        }}
                      >
                        <span style={{ fontSize: '10px' }}>{collapsed ? '▸' : '▾'}</span>
                        {STATUS_LABEL[s]} ({items.length})
                      </button>
                      {collapsed ? null : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {items.map((t, idx) => {
                          const rowStyle: React.CSSProperties = {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            backgroundColor: theme.background,
                            border: `1px solid ${theme.border}`,
                            borderRadius: '6px',
                          };
                          if (t.id == null) {
                            return (
                              <div key={`no-id-${idx}`} style={{ ...rowStyle, color: theme.text, fontSize: '14px' }}>
                                {t.title || '(untitled)'}
                              </div>
                            );
                          }
                          const tid = t.id;
                          const aiRes = inlineAiResult[tid];
                          return (
                            <div key={tid} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={rowStyle}>
                              <button
                                type="button"
                                onClick={() => openTask(tid)}
                                title="Open this task in-place to read the AI output and act"
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  textAlign: 'left',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  color: theme.text,
                                  fontSize: '14px',
                                }}
                              >
                                {t.title || '(untitled)'}
                              </button>
                              {t.ai_team_member && person && t.ai_team_member.toLowerCase() !== person.toLowerCase() && (
                                <span
                                  title={`You created this ticket; it's assigned to ${t.ai_team_member}. It shows here so you can track it from your lane.`}
                                  style={{
                                    flexShrink: 0,
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    color: theme.textSecondary,
                                    border: `1px solid ${theme.border}`,
                                    borderRadius: '999px',
                                    padding: '1px 7px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  for {t.ai_team_member}
                                </span>
                              )}
                              {t.created_by_ai && (
                                <span
                                  title={`Created automatically by an AI rep${
                                    t.created_by ? ` (${t.created_by})` : ''
                                  }, not by you. Your reps spawn tasks as they work.`}
                                  style={{
                                    flexShrink: 0,
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    color: theme.textSecondary,
                                    border: `1px solid ${theme.border}`,
                                    borderRadius: '999px',
                                    padding: '1px 7px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  🤖 AI
                                </span>
                              )}
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                                <button
                                  type="button"
                                  onClick={() => handleInlineAi(tid)}
                                  disabled={inlineAiTaskId === tid}
                                  title="Let your AI rep handle this for you"
                                  style={{
                                    background: 'none',
                                    border: `1px solid ${theme.primary}`,
                                    borderRadius: '6px',
                                    color: theme.primary,
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    padding: '4px 8px',
                                    cursor: inlineAiTaskId === tid ? 'wait' : 'pointer',
                                  }}
                                >
                                  {inlineAiTaskId === tid ? '…' : '🤖 AI'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleInlineDismiss(tid)}
                                  title="Dismiss — remove this from your lane"
                                  aria-label="Dismiss"
                                  style={{
                                    background: 'none',
                                    border: `1px solid ${theme.border}`,
                                    borderRadius: '6px',
                                    color: theme.textSecondary,
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    padding: '4px 9px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ✕
                                </button>
                              </span>
                            </div>
                            {/* AI-handle result, surfaced inline so the click never looks dead. */}
                            {aiRes && (
                              <div
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: '6px',
                                  border: `1px solid ${aiRes.outcome === 'handled' ? theme.success : theme.primary}`,
                                  backgroundColor:
                                    (aiRes.outcome === 'handled' ? theme.success : theme.primary) + '12',
                                }}
                              >
                                <div
                                  style={{
                                    color: aiRes.outcome === 'handled' ? theme.success : theme.primary,
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    marginBottom: aiRes.draft ? '4px' : 0,
                                  }}
                                >
                                  {aiRes.message}
                                </div>
                                {aiRes.draft && (
                                  <div style={{ color: theme.text, fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                                    {aiRes.draft}
                                  </div>
                                )}
                                {aiRes.outcome === 'drafted' && (
                                  <div style={{ color: theme.textSecondary, fontSize: '11px', marginTop: '6px' }}>
                                    Open the task to review the full draft and respond.
                                  </div>
                                )}
                              </div>
                            )}
                            </div>
                          );
                        })}
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Relevant initiatives ----------------------------------------- */}
      {person && (
        <div style={cardStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '4px',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <h2 style={{ color: theme.text, margin: 0, fontSize: '18px' }}>Relevant initiatives</h2>
            <Link to="/initiatives" style={{ color: theme.primary, fontSize: '14px', fontWeight: 600 }}>
              Open initiatives →
            </Link>
          </div>
          <p style={{ color: theme.textSecondary, margin: '0 0 14px', fontSize: '13px' }}>
            {isJoshua
              ? 'All initiatives (Founder sees everything).'
              : `Initiatives in your ${personDomain} domain.`}
          </p>

          {initiativesLoading && (
            <div style={{ color: theme.textSecondary, fontSize: '14px' }}>Loading initiatives…</div>
          )}

          {!initiativesLoading && initiativesError && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#ef444422',
                border: '1px solid #ef4444',
                borderRadius: '6px',
                color: '#ef4444',
                fontSize: '13px',
              }}
            >
              {initiativesError}
            </div>
          )}

          {!initiativesLoading && !initiativesError && relevantInitiatives && relevantInitiatives.length === 0 && (
            <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
              {initiatives && initiatives.length > 0 && !isJoshua
                ? `No initiatives in your ${personDomain} domain yet.`
                : 'No initiatives configured yet.'}
            </div>
          )}

          {!initiativesLoading && !initiativesError && relevantInitiatives && relevantInitiatives.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {relevantInitiatives.map((init, idx) => (
                <Link
                  key={init.id ?? idx}
                  to="/initiatives"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: theme.background,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    textDecoration: 'none',
                  }}
                >
                  <span style={{ color: theme.text, fontSize: '14px', fontWeight: 600 }}>
                    {init.name || init.id || '(unnamed initiative)'}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {init.quest_id && <OpenInQuestLink questId={init.quest_id} color={theme.primary} fontSize="12px" />}
                    {init.status && (
                      <span
                        style={{
                          padding: '3px 10px',
                          borderRadius: '999px',
                          backgroundColor: theme.surface,
                          border: `1px solid ${theme.border}`,
                          color: theme.textSecondary,
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {init.status}
                      </span>
                    )}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* The task detail modal is the SHARED app-root <TaskModal/> (opened via
          openTask). It dispatches a `taskmodal:changed` event after any action
          that changes/removes a task; we reload the lane in response (see the
          effect above). No navigation away from this page. */}

      {/* Mobile responsive: collapse multi-column grids */}
      <style>{`
        @media (max-width: 768px) {
          .role-feature-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PersonLane;
